<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';

.auth-wrapper {
  .login-bg {
    background-size: cover;
    background-position: center;
  }
}
</style>

<template>
  <div class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0">
      <!-- Left image -->
      <b-col
        lg="8"
        :style="{ 'background-image': 'url(' + bgImage + ')' }"
        class="d-none d-lg-flex align-items-center p-5 login-bg"
      />
      <!-- /Left image -->

      <!-- 2FA -->
      <b-col lg="4" class="d-flex align-items-center auth-bg px-2 p-lg-5">
        <b-col sm="8" md="6" lg="12" class="px-xl-2 mx-auto">
          <b-card-title title-tag="h2" class="font-weight-bold mb-1">
            Two-factor authentication
          </b-card-title>

          <b-card-text class="mb-2">
            Enter the 6-digit code sent to your email address.
          </b-card-text>

          <b-card-text v-if="hasCodeError" class="mt-2" type="warning" style="color: red">
            {{ tooManyAttempts
              ? 'You have made too many attempts and are temporarily locked out. Please wait a while before retrying.'
              : 'The code provided is either invalid or expired. Please try again.' }}
          </b-card-text>

          <b-card-text v-if="hasInternalError" class="mt-2" type="warning" style="color: red">
            Something went wrong, probably at our end. Please contact your support team.
          </b-card-text>

          <validation-observer ref="twoFaValidation">
            <b-form class="auth-login-form mt-2" @submit.prevent>
              <!-- Code -->
              <b-form-group label="Authentication code" label-for="twofa-code">
                <validation-provider #default="{ errors }" name="2FA code" rules="required">
                  <b-form-input
                    id="twofa-code"
                    v-model="code"
                    :state="errors.length > 0 ? false : null"
                    name="twofa-code"
                    placeholder="123456"
                    inputmode="numeric"
                    autocomplete="one-time-code"
                    @keyup.enter="validationForm"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Actions -->
              <b-button
                type="submit"
                variant="primary"
                block
                :disabled="isVerifying"
                @click="validationForm"
              >
                {{ isVerifying ? 'Verifying…' : 'Verify' }}
              </b-button>

              <div class="d-flex justify-content-between align-items-center mt-2">
                <b-button variant="link" class="p-0 text-danger" @click="cancelTwoFa">
                  Cancel
                </b-button>
              </div>
            </b-form>
          </validation-observer>
        </b-col>
      </b-col>
      <!-- /2FA -->
    </b-row>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { required } from '@validations'
import { logger } from '@/util/logger'
import PrisonUsersService from '@/services/PrisonUsersService';
import useJwt from '@/auth/jwt/useJwt';

export default {
  name: 'TwoFactorVerify',
  data() {
    return {
      // ui state
      hasCodeError: false,
      hasInternalError: false,
      tooManyAttempts: false,
      isVerifying: false,
      isResending: false,

      // form
      code: '',

      // assets
      // eslint-disable-next-line global-require
      bgImage: require('@/assets/images/pages/two-fa-background.png'),

      // validation
      required,
    }
  },
  computed: {
    ...mapState({
      region: state => state.region,
    }),
  },
  mounted() {
    // Ensure we have a JWT token set and in use
    const token = useJwt.getToken()
    if (!token) {
      this.$router.push({ name: 'login' })
    }
  },
  methods: {
    validationForm() {
      this.$refs.twoFaValidation.validate().then(success => {
        if (!success) return
        this.verifyCode()
      })
    },
    normaliseCode(raw) {
      // Skeleton: strip spaces/hyphens; keep digits only
      return String(raw || '').replace(/[\s-]/g, '')
    },
    async verifyCode() {
      this.hasCodeError = false
      this.hasInternalError = false
      this.tooManyAttempts = false
      this.isVerifying = true

      const code = this.normaliseCode(this.code)

      try {
        const { data } = await PrisonUsersService.Authorise(code)

        // Set new authorised access token and refresh token
        useJwt.setToken(data.token)
        useJwt.setRefreshToken(data.refreshToken)

        // Fetch user info
        this.getUser()
      } catch (err) {
        logger.error('2FA verify failed', err, {}, 'TwoFA:verifyCode')
        this.hasCodeError = err.response.status < 500
        this.hasInternalError = err.response.status >= 500
        this.tooManyAttempts = err.response.status === 429
      } finally {
        this.isVerifying = false
      }
    },
    cancelTwoFa() {
      // Clear any user
      this.$store.dispatch('auth/clearLocalUser')
      this.$store.commit('auth/SET_USER', null)

      // Go back to the login page
      this.$router.push({ name: 'login' })
    },
    async getUser() {
      try {
        const { data } = await PrisonUsersService.getUser()
        // Check the user has correct type
        if (data.type !== 'WebPortal') {
          logger.warn('User is not of correct type', null, {
            data,
            expectedType: 'WebPortal',
          }, 'Login:getUser')
          // Clear the token and display an error
          this.$store.dispatch('auth/clearLocalUser')
          this.$store.commit('auth/SET_USER', null)
          this.isLoggingIn = false
          this.hasLoginError = true
          this.hasInternalError = false
        } else {
          this.$store.dispatch('auth/setLocalUser', data)
          // Push to dashboard
          this.$router.push({ name: 'dashboard' })
        }
      } catch (err) {
        logger.error('Error getting user', err, { username: this.username }, 'TwoFactor:getUser')
        this.isVerifying = false
        this.hasCodeError = err.response.status < 500
        this.hasInternalError = err.response.status >= 500
      }
    },
  },
}
</script>
